import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Dialog, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import BaseCodeInputMonacoEditor from './BaseCodeInputMonacoEditor';

/**
 * This a CodeMonaco based component.
 *
 * onChange (value, errors) => {} value: String. errors: String array.
 * Each item is an error messages with the line and column.
 *
 * value JSON object or a string result of JSON.stringify.
 * Otherwise, an error will be thrown.
 *
 * height Just some CSS valid height property.
 */
const JsonCodeInputMonacoEditor = (props) => {
    const { value, onChange, height, width, title, allowMaximize = false, readOnly = false } = props;
    const [isMaximized, setIsMaximized] = useState(false);
    const [content, setContent] = useState('');

    // Track if user is actively editing to prevent external resets
    const isUserEditingRef = useRef(false);
    const debounceTimeoutRef = useRef(null);
    const lastParsedValueRef = useRef(null);

    useEffect(() => {
        // Only update content if user is not actively editing AND this is a meaningful external change
        if (isUserEditingRef.current) {
            return;
        }

        const valueType = typeof value;
        let newContent;

        if (value === undefined || value === null) {
            // Don't reset to empty if we already have content
            if (content.trim() !== '') {
                return;
            }
            newContent = '';
        } else if (valueType === 'object') {
            newContent = JSON.stringify(value, null, '    ');
        } else if (valueType === 'string') {
            newContent = value;
        } else {
            throw new Error(
                `JsonCodeInputMonacoEditor expects a JSON or a stringified JSON as value. ` +
                    `GIVEN VALUE: ${value}' TYPE: ${valueType}'`,
            );
        }

        // Only update if content is different AND this represents a real external change
        // Don't update if we're going from non-empty to empty
        const isGoingFromContentToEmpty = content.trim() !== '' && newContent === '';
        if (newContent !== content && !isGoingFromContentToEmpty) {
            setContent(newContent);
            lastParsedValueRef.current = value;
        }
    }, [value, content]);

    const handleEditorBlur = useCallback(() => {
        // If editor is empty on blur, reset to empty braces
        if (content.trim() === '') {
            setContent('{}');
            lastParsedValueRef.current = {};
            onChange({}, []);
        }
        isUserEditingRef.current = false;
    }, [content, onChange]);

    const debouncedParseAndPropagate = useCallback(
        (newContent) => {
            // Clear existing timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            // Set new timeout for parsing
            debounceTimeoutRef.current = setTimeout(() => {
                try {
                    const trimmedContent = newContent.trim();
                    let parsedJson;

                    if (trimmedContent === '') {
                        // Allow empty content during editing, don't auto-reset to {}
                        parsedJson = null;
                    } else {
                        parsedJson = JSON.parse(trimmedContent);
                    }

                    // Only call onChange if the parsed value actually changed
                    if (JSON.stringify(parsedJson) !== JSON.stringify(lastParsedValueRef.current)) {
                        lastParsedValueRef.current = parsedJson;
                        // Don't call onChange for null values to prevent parent from sending default values back
                        if (parsedJson !== null) {
                            onChange(parsedJson, []); // No errors
                        }
                    }
                } catch (error) {
                    // Only propagate error state, don't change the value
                    // This allows user to continue editing invalid JSON
                    onChange(lastParsedValueRef.current, ['Invalid JSON: ' + error.message]);
                }

                // User finished editing after debounce period (but not on blur)
                isUserEditingRef.current = false;
            }, 300); // 300ms debounce
        },
        [onChange],
    );

    const propagateChange = (newContent, codeErrors) => {
        // Mark that user is actively editing
        isUserEditingRef.current = true;

        // Always update the visual content immediately
        setContent(newContent);

        // Debounce the parsing and propagation
        debouncedParseAndPropagate(newContent);
    };

    const handleMaximize = () => {
        setIsMaximized(true);
    };

    const handleMinimize = () => {
        setIsMaximized(false);
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const editor = (
        <BaseCodeInputMonacoEditor
            height={height || '200px'}
            width={width}
            defaultLanguage="json"
            value={content}
            onChange={(newContent, errors) => propagateChange(newContent, errors)}
            onBlur={handleEditorBlur}
            readOnly={readOnly}
        />
    );

    return (
        <div style={{ margin: '0 0 10px 0' }}>
            {allowMaximize ? (
                <Stack spacing={1}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton onClick={handleMaximize} size="small">
                            <FullscreenIcon />
                        </IconButton>
                    </div>
                    <div>{editor}</div>
                </Stack>
            ) : (
                <div>{editor}</div>
            )}

            {/* Dialog for maximized view */}
            <Dialog open={isMaximized} onClose={handleMinimize} maxWidth="xl" fullWidth>
                <DialogTitle
                    sx={{
                        m: 0,
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {title || 'JSON Editor'}
                    <IconButton aria-label="close" onClick={handleMinimize} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <BaseCodeInputMonacoEditor
                        height="80vh"
                        width="100%"
                        defaultLanguage="json"
                        value={content}
                        onChange={(newContent, errors) => propagateChange(newContent, errors)}
                        onBlur={handleEditorBlur}
                        readOnly={readOnly}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default JsonCodeInputMonacoEditor;
